import { ChangeDetectionStrategy, Component, Input, inject, Output, EventEmitter } from '@angular/core';
import { Subscription, takeUntil } from 'rxjs';
import { TuiDialogContext, TuiDialogService, TuiSizeL } from '@taiga-ui/core';
import { PolymorpheusContent } from '@taiga-ui/polymorpheus';
import { BaseResizableComponent } from '@src/app/modules/base';

import {
  CategoriesStoreKeys,
  LoyaltyProgramFilterService,
  LoyaltyProgramPermService,
  LpCategoriesStoreService,
  LpUserProfileService,
} from '../../services';
import { CategoryChangeEvent } from '../category-cell';
import { NEW_CATEGORY_ITEM } from '../../constants';
import { GroupItem, LoyaltyProgramFilterData } from '../../types';

@Component({
  selector: 'app-categories-button',
  templateUrl: './categories-button.component.html',
  styleUrls: ['./categories-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoriesButtonComponent extends BaseResizableComponent {
  private readonly loyaltyProgramFilterService = inject(LoyaltyProgramFilterService);
  private readonly lpUserProfileService = inject(LpUserProfileService);
  private readonly lpCategoriesStore = inject(LpCategoriesStoreService);
  private readonly permService = inject(LoyaltyProgramPermService);
  private readonly dialogService = inject(TuiDialogService);

  @Input() size: TuiSizeL = 'l';

  @Output() applyFilterAndChangeRoute = new EventEmitter<LoyaltyProgramFilterData>();

  categories?: ReadonlyArray<GroupItem>;

  readonly newCategoryItem = NEW_CATEGORY_ITEM;
  readonly allowActiveCollectionsViewing = this.permService.allowActiveCollectionsViewing;

  private categoriesDialog$?: Subscription;

  override ngOnInit(): void {
    super.ngOnInit();

    this.loyaltyProgramFilterService.getCategories(true);

    this.lpCategoriesStore
      .getDataObservable(CategoriesStoreKeys.InUse)
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(categories => {
        this.categories = categories;

        this.cdr.markForCheck();
      });
  }

  onSelectMainCategory(category: CategoryChangeEvent) {
    this.closeCategoriesDialog();

    this.applyFilterAndChangeRoute.emit({
      categories: category.items,
    });
  }

  openFavoriteCategoriesDialog() {
    const selectedCategoriesIds = this.lpUserProfileService.authUser$.value?.favoriteCategories;

    this.lpUserProfileService.showFavoriteCategoriesDialog(selectedCategoriesIds).subscribe(favoriteCategoriesIds => {
      if (favoriteCategoriesIds) {
        this.lpUserProfileService.updateUserFavoriteCategories(favoriteCategoriesIds).subscribe(() => {
          this.loyaltyProgramFilterService.getCategories(true, true);
        });
      }
    });
  }

  protected openCategoriesDialog(content: PolymorpheusContent<TuiDialogContext>) {
    this.categoriesDialog$ = this.dialogService
      .open<boolean>(content, { appearance: this.isMobile ? 'horizontal-zero-padding' : undefined })
      .pipe(takeUntil(this.destroyed$$))
      .subscribe();
  }

  private closeCategoriesDialog() {
    this.categoriesDialog$?.unsubscribe();
  }
}
